/* eslint-disable i18next/no-literal-string -- i18n deferred to v2 per plan */
/**
 * HostUpgradeStagePanel renders per-stage status for host_upgrade operations.
 *
 * Reads operation logs via useOperationLogs, pipes through parseStageMarkers,
 * and renders four stage cards with status badges. Raw output lives in the
 * Logs tab (parent passes onViewLogs to switch tabs from a stage card).
 */
import { useMemo } from 'react'
import { Card, Badge, Button } from '@agion/ui'
import { ExternalLink, Loader2 } from 'lucide-react'
import { useOperationLogs } from '@/hooks/infra_use_operation_logs'
import { parseStageMarkers, type StageStatusEntry, type StageStatus } from './parseStageMarkers'
import { cn } from '@/core/infra_utils'

interface Props {
  operationId: string
  /** Switch to the Logs tab. Optional: omit to hide the per-stage "View logs" link. */
  onViewLogs?: (stage: string) => void
}

const STAGE_LABELS: Record<string, string> = {
  precheck: 'Pre-check',
  apt: 'APT',
  postinst: 'Postinst / Flyway',
  verify: 'Verify',
}

const STATUS_BADGE_CONFIG: Record<StageStatus, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
  pending: { variant: 'default', label: 'Pending' },
  running: { variant: 'info', label: 'Running' },
  success: { variant: 'success', label: 'Success' },
  failed: { variant: 'error', label: 'Failed' },
  skipped: { variant: 'warning', label: 'Skipped' },
}

interface StageCardProps {
  entry: StageStatusEntry
  onViewLogs?: (stage: string) => void
}

function StageCard({ entry, onViewLogs }: StageCardProps) {
  const config = STATUS_BADGE_CONFIG[entry.status]

  // Failed cards link directly to the Logs tab so the operator can see what
  // went wrong without scrolling through unrelated stage output.
  const showViewLogsLink = entry.status === 'failed' && onViewLogs

  let badgeLabel = config.label
  if (entry.status === 'failed' && entry.exitCode !== undefined) {
    badgeLabel = `Failed (exit ${entry.exitCode})`
  }

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {STAGE_LABELS[entry.name] ?? entry.name}
        </span>
        <Badge
          variant={config.variant}
          className={cn(
            'flex items-center gap-1',
            entry.status === 'running' && 'stage-running-badge'
          )}
        >
          {entry.status === 'running' && (
            <Loader2 size={12} className="animate-spin" aria-label="Running spinner" aria-hidden={false} />
          )}
          {badgeLabel}
        </Badge>
      </div>

      {entry.status === 'skipped' && entry.reason && (
        <p className="text-xs text-amber-600 dark:text-amber-400" title={entry.reason}>
          Reason: {entry.reason}
        </p>
      )}

      {showViewLogsLink && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewLogs(entry.name)}
          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1 h-auto p-0 self-start"
          aria-label={`View logs for ${STAGE_LABELS[entry.name] ?? entry.name}`}
        >
          View logs <ExternalLink size={12} />
        </Button>
      )}
    </div>
  )
}

export function HostUpgradeStagePanel({ operationId, onViewLogs }: Props) {
  const { logs } = useOperationLogs({ operationId, enabled: true })

  const stages = useMemo(() => parseStageMarkers(logs), [logs])

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
          Upgrade Stages
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {stages.map(entry => (
            <StageCard key={entry.name} entry={entry} onViewLogs={onViewLogs} />
          ))}
        </div>
      </div>
    </Card>
  )
}
